import { gql } from '@apollo/client';
import { Input } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentPressroom } from '../../../contexts/CurrentPressroomContext';
import {
  type NewsroomDataCompanyInformation,
  useNewsroomSetup,
} from '../../../contexts/NewsroomSetupContext';
import {
  useSeenOnboardingWizardFromFooterMutation,
  useUpdateCompanyInformationOnboardingMutation,
} from '../../../generated/hooks';
import useHotjarTrigger from '../../../hooks/useHotjarTrigger';
import { track } from '../../../lib/tracker';
import Footer from './Footer';
import Header from './Header';
import { Form, StepForm, StepSectionWrapper } from './styles';

gql`
  fragment CompanyInfo on NewsroomSettings {
    id
    companyName(externalPrefill: true)
    street(externalPrefill: true)
    postalCode(externalPrefill: true)
    city(externalPrefill: true)
    country(externalPrefill: true)
  }

  mutation UpdateCompanyInformationOnboarding(
    $newsroomId: ID!
    $companyName: String
    $street: String
    $postalCode: String
    $city: String
    $country: String
  ) {
    newsroomSettingsUpdate(
      input: {
        newsroomId: $newsroomId
        companyName: $companyName
        street: $street
        postalCode: $postalCode
        city: $city
        country: $country
      }
    ) {
      error {
        ... on NewsroomSettingsUpdateValidationError {
          companyName
          street
          postalCode
          city
          country
        }
      }
      newsroomSettings {
        id
        ...CompanyInfo
      }
    }
  }
`;

const CompanyInformation = () => {
  const [t] = useTranslation();
  const { showFailureToast } = useToasts();
  const { pressroomId } = useCurrentPressroom();
  const { showReadyToGoLive, newsroomData, handleNewsroomDataFieldChange, setNewsroomSetupStep } =
    useNewsroomSetup();

  const formRef = useRef<HTMLFormElement>(null);
  const initialValues = useRef<NewsroomDataCompanyInformation>(newsroomData['company-information']);

  const [validationError, setValidationError] = useState('');

  const [seenOnboardingWizard, { loading: seenLoading }] =
    useSeenOnboardingWizardFromFooterMutation({
      onCompleted: () => {
        setNewsroomSetupStep(undefined);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      variables: {
        pressroomId,
      },
    });

  const [updateSettings, { loading }] = useUpdateCompanyInformationOnboardingMutation({
    onCompleted: async response => {
      if (
        response?.newsroomSettingsUpdate?.error?.__typename ===
        'NewsroomSettingsUpdateValidationError'
      ) {
        setValidationError(
          response?.newsroomSettingsUpdate?.error?.companyName ?? t('common_general_error'),
        );
        return;
      }
      const dataChanged = !isEqual(initialValues.current, newsroomData['company-information']);
      track({
        event: 'Newsroom Setup: Company Info - Proceed',
        traits: {
          step: 4,
          dataChanged,
        },
      });
      if (showReadyToGoLive) {
        setNewsroomSetupStep('go-live');
      } else {
        await seenOnboardingWizard();
      }
    },
    onError: () => showFailureToast(t('common_general_error')),
    variables: {
      newsroomId: pressroomId,
      companyName: newsroomData['company-information'].companyName,
      street: newsroomData['company-information'].street,
      postalCode: newsroomData['company-information'].postalCode,
      city: newsroomData['company-information'].city,
      country: newsroomData['company-information'].country,
    },
  });

  useEffect(() => {
    if (validationError || formRef.current) {
      formRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [validationError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ?
  useEffect(() => {
    const prepopulated = Boolean(
      newsroomData['company-information'].companyName && newsroomData['company-information'].street,
    );
    track({
      event: 'Newsroom Setup: Company Info',
      traits: { step: 4, prepopulated },
    });
  }, []);

  useHotjarTrigger('onboarding_wizard_start');

  return (
    <StepForm
      ref={formRef}
      onSubmit={e => {
        e.preventDefault();
        updateSettings();
      }}
    >
      <StepSectionWrapper>
        <Header currentStep={4} heading={t('onboarding-company-information_title')} />
        <Form>
          <Input
            error={validationError}
            label={t('newsroom_settings-general_settings-company_name-input_label')}
            id="companyName"
            value={newsroomData['company-information'].companyName}
            onChange={e =>
              handleNewsroomDataFieldChange('company-information', 'companyName', e.target.value)
            }
          />
          <Input
            label={t('newsroom_settings-footer_settings-street-input_label')}
            value={newsroomData['company-information'].street}
            onChange={e =>
              handleNewsroomDataFieldChange('company-information', 'street', e.target.value)
            }
          />
          <Input
            label={t('newsroom_settings-footer_settings-post_code-input_label')}
            value={newsroomData['company-information'].postalCode}
            onChange={e =>
              handleNewsroomDataFieldChange('company-information', 'postalCode', e.target.value)
            }
          />
          <Input
            label={t('common_city')}
            value={newsroomData['company-information'].city}
            onChange={e =>
              handleNewsroomDataFieldChange('company-information', 'city', e.target.value)
            }
          />
          <Input
            label={t('common_country')}
            value={newsroomData['company-information'].country}
            onChange={e =>
              handleNewsroomDataFieldChange('company-information', 'country', e.target.value)
            }
          />
        </Form>
        <Footer
          isSaving={loading || seenLoading}
          canContinue={
            !!(
              newsroomData['company-information'].companyName ||
              newsroomData['company-information'].street ||
              newsroomData['company-information'].postalCode ||
              newsroomData['company-information'].city ||
              newsroomData['company-information'].country
            )
          }
          showReadyToGoLive={showReadyToGoLive}
          previousStep="company-description"
          nextStep="go-live"
        />
      </StepSectionWrapper>
    </StepForm>
  );
};

export default CompanyInformation;
